import { observer } from 'mobx-react-lite'
import React, { useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { type Rect } from '@xyflow/react'
import { SelectField } from 'shared/ui/Select/SelectField/SelectField'
import { Avatar, Button, Icon, IconButton, Tooltip, Typography } from 'shared/ui'
import { makeTextFieldIcon } from 'shared/ui/TextField'
import { dropDownStore } from 'shared/ui/Dropdown'
import { SendFromMulti } from 'widgets/SendFromMulti'
import { type IEmailToSmsGroup } from 'pages/settings/pages/emailToSms/store/emailToSmsPageStore'
import { useEmailToSmsPageStorePageContext } from 'pages/settings/pages/emailToSms/context/emailToSmsContext'
import { EmailToSmsActionStore } from 'pages/settings/pages/emailToSms/store/emailToSmsActionStore'
import styles from './styles.module.scss'

export const EmailToSmsList = observer(() => {
  const actionStore = useMemo(() => new EmailToSmsActionStore(), [])
  const {
    groups,
    members,
    userInfo,
    updateInboxes,
    updateEmail,
    deleteGroup,
    removeEmptyGroup,
    isValidEmail,
    setGroupSearchValue,
  } = useEmailToSmsPageStorePageContext()
  const openStateRef = useRef<Record<string, boolean>>({})

  useEffect(() => {
    openStateRef.current = {}
  }, [groups])

  const handleInboxToggle = (group: IEmailToSmsGroup, isOpen: boolean) => {
    const key = String(group.email)

    if (!openStateRef.current[key]) {
      openStateRef.current[key] = true
      return
    }

    updateInboxes(group, isOpen)
  }

  const membersWithEmail = useMemo(() => {
    return members.map((member) => ({
      ...member,
      labelContent: (
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            ellipsis
            variant='body-md-regular'
            color='var(--content-primary-primary)'
            ariaLabel={'Contact name'}
          >
            {member.data?.name || member.label}
          </Typography>
          <Typography
            className={styles.labelContentEmail}
            color='var(--content-primary-tertiary)'
            ariaLabel={'Contact e-mail'}
          >
            {member.data?.email}
          </Typography>
        </div>
      ),
      className: styles.selectOptionWrap,
    }))
  }, [members])

  const CustomNotFound = ({ group }: { group: IEmailToSmsGroup }) => {
    const search = group.searchValue

    if (isValidEmail(search)) {
      return (
        <Button
          text={`Add “${search}”`}
          typeBtn={'menuItem'}
          className={styles.noResult}
          contained={'secondary'}
          fullWidth
          icon='add'
          justifyContent='left'
          onClick={() => {
            dropDownStore.allHide()
            updateEmail(group)
          }}
        />
      )
    }
    return <div>No results found</div>
  }

  return (
    <div className={styles.container}>
      {groups.map((group, _, groups) => {
        const emailOwnerInfo = userInfo(group.emailValue || '')
        const searchValue = group.searchValue
        const filteredMembers = !searchValue
          ? membersWithEmail
          : membersWithEmail.filter((member) => {
              const email = member.data?.email || ''
              const name = member.data?.name || member.label || ''
              return (
                email.toLowerCase().includes(searchValue.toLowerCase()) ||
                name.toLowerCase().includes(searchValue.toLowerCase())
              )
            })

        return (
          <div key={group.email} className={styles.row}>
            <SendFromMulti
              store={group.inboxStore}
              triggerProps={{
                selectedOptionsCount: 1,
                emptyPlaceholder: 'Select Salesmsg inbox',
                isShowIcon: false,
              }}
              textFieldPlaceholderProps={{
                className: styles.inboxPlaceholderWrap,
                variant: 'stroke',
                rightActions: [
                  makeTextFieldIcon({
                    icon: 'chevronDown',
                    color: 'var(--content-primary-tertiary)',
                    margin: 'auto',
                  }),
                ],
              }}
              dropdownProps={{
                placeholder: 'Search inbox',
                onChangeOpen: (isOpen) => handleInboxToggle(group, isOpen),
                width: '318px',
              }}
              popperProps={{
                offset: [-4, -40],
              }}
              dropdownGroupProps={{
                showSelectAllButton: true,
                stylesWrap: {
                  padding: '4px',
                },
              }}
            />

            <Icon icon={'arrowRight'} boxRounded={true} className={styles.separator} />

            <SelectField
              setValue={(value) => updateEmail(group, value)}
              value={emailOwnerInfo?.id || group.emailValue}
              items={filteredMembers}
              btnProps={{
                size: 'medium',
                style: {
                  height: '40px',
                  paddingRight: '8px',
                },
                text: group.emailValue ? (
                  <Typography ariaLabel='name' className={styles.selectLine}>
                    {emailOwnerInfo?.avatarProps?.info && (
                      <Avatar size={16} info={emailOwnerInfo?.avatarProps.info} />
                    )}
                    {!emailOwnerInfo?.avatarProps?.info && (
                      <Avatar
                        size={16}
                        info={{
                          icon: 'personFilled',
                        }}
                      />
                    )}
                    <span className={styles.selectLineEmail}>
                      {emailOwnerInfo?.data?.email || group.emailValue}
                    </span>
                  </Typography>
                ) : (
                  <Typography ariaLabel='Enter e-mail or name' className={styles.selectLine}>
                    <span
                      className={classNames(
                        styles.selectLineEmail,
                        styles.selectLineEmailPlaceholder
                      )}
                    >
                      Enter e-mail or name
                    </span>
                  </Typography>
                ),
              }}
              search={searchValue}
              onSearch={(value) => {
                setGroupSearchValue(group, value)
              }}
              dropdownProps={{
                width: '420px',
                onChangeOpen: (isOpen) => {
                  if (isOpen) setGroupSearchValue(group, '')
                },
                propsDropdownList: {
                  notFoundText: <CustomNotFound group={group} />,
                },
                PopperProps: {
                  modifiers: [
                    {
                      name: 'offset',
                      options: {
                        offset: ({
                          reference,
                        }: {
                          reference: Rect & { offset?: [number, number] }
                        }) => {
                          return reference?.offset ?? [-4, -reference.height]
                        },
                      },
                    },
                  ],
                },
              }}
            />

            <div
              className={classNames(styles.deleteIconWrapper, {
                [styles.deleteIconHidden]: groups.length === 1 && !group.emailValue,
              })}
            >
              <Tooltip label={'Delete mapping'} placement={'top'}>
                <IconButton
                  onClick={() => {
                    if (group.emailValue === null) {
                      removeEmptyGroup()
                    } else {
                      actionStore.onDeleteMapping(deleteGroup, group)
                    }
                  }}
                  color={'tertiary'}
                  icon={'delete'}
                  variant={'icon'}
                  ariaLabel='Delete mapping'
                />
              </Tooltip>
            </div>
          </div>
        )
      })}
    </div>
  )
})
